import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSchemeOptions } from '@/hooks/useSchemeOptions';
import type { SchemeOption } from '@/hooks/useSchemeOptions';
import { useAnchoredPopover } from '@/hooks/useAnchoredPopover';
import { TabBar, Button } from '@/components/ui';
import { ORDER_CALC_TYPE_MUTUAL_FUND } from '@/types/client';
import s from './SchemeFilterPanel.module.css';
import { SCHEME_REGION_DOMESTIC, SCHEME_REGION_GLOBAL } from '@/config/constants';

export type { SchemeOption };

type RegionType = 'indian' | 'international';

export interface SchemeFilterPanelProps {
  onFilterChange: (regionSchemes: SchemeOption[], hiddenSchemeIds: Set<string>) => void;
  onError: (error: string | null) => void;
  onRegionChange?: () => void;
}

const HIDDEN_CODES_BY_DEFAULT = new Set(['IEF', 'IOE']);

const SchemeFilterPanel: React.FC<SchemeFilterPanelProps> = ({
  onFilterChange,
  onError,
  onRegionChange,
}) => {
  const { schemeOptions: schemes, error } = useSchemeOptions();
  const [regionType, setRegionType] = useState<RegionType>('indian');
  // null until the user changes something — until then the defaults below apply.
  const [userHiddenIds, setHiddenSchemeIds] = useState<Set<string> | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const closeFilterPanel = useCallback(() => setShowFilterPanel(false), []);
  const { anchorRef: toggleBtnRef, panelRef, pos } = useAnchoredPopover(showFilterPanel, closeFilterPanel, 640);

  const onFilterChangeRef = useRef(onFilterChange);
  useEffect(() => { onFilterChangeRef.current = onFilterChange; });
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; });
  const onRegionChangeRef = useRef(onRegionChange);
  useEffect(() => { onRegionChangeRef.current = onRegionChange; });

  useEffect(() => { onErrorRef.current(error); }, [error]);

  // Mutual fund schemes and IEF/IOE are hidden by default. Derived in the same
  // render the schemes arrive, not set by an effect afterwards: the effect made
  // the page see "every scheme visible" first and then a second, debounced
  // change, which fetched twice and delayed the first load.
  const hiddenSchemeIds = useMemo(
    () => userHiddenIds ?? new Set(
      schemes
        .filter(sc => sc.orderCalcType === ORDER_CALC_TYPE_MUTUAL_FUND || HIDDEN_CODES_BY_DEFAULT.has(sc.label.split(' - ')[0]))
        .map(sc => sc.value),
    ),
    [userHiddenIds, schemes],
  );

  const regionSchemes = useMemo(
    () => schemes.filter(sc => regionType === 'indian' ? sc.region === SCHEME_REGION_DOMESTIC : sc.region === SCHEME_REGION_GLOBAL),
    [schemes, regionType],
  );

  const schemesByStrategy = useMemo(() => {
    const map: Record<string, { key: string; display: string; schemes: SchemeOption[] }> = {};
    regionSchemes.forEach(sc => {
      const key = sc.investmentStrategy || '__none__';
      if (!map[key]) map[key] = { key, display: sc.investmentStrategyDisplay, schemes: [] };
      map[key].schemes.push(sc);
    });
    return Object.values(map);
  }, [regionSchemes]);

  useEffect(() => {
    onFilterChangeRef.current(regionSchemes, hiddenSchemeIds);
  }, [regionSchemes, hiddenSchemeIds]);

  const handleRegionChange = (region: RegionType) => {
    setRegionType(region);
    onRegionChangeRef.current?.();
  };

  const toggleScheme = (id: string) => {
    setHiddenSchemeIds(prev => {
      const next = new Set(prev ?? hiddenSchemeIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleStrategy = (ids: string[]) => {
    setHiddenSchemeIds(prev => {
      const next = new Set(prev ?? hiddenSchemeIds);
      const allHidden = ids.every(id => next.has(id));
      if (allHidden) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const visibleCount = regionSchemes.filter(sc => !hiddenSchemeIds.has(sc.value)).length;

  return (
    <>
      <TabBar
        ariaLabel="Scheme region"
        align="end"
        value={regionType}
        onChange={value => handleRegionChange(value as RegionType)}
        tabs={[
          { value: 'indian', label: 'All Indian' },
          { value: 'international', label: 'All International' },
        ]}
      />
      <Button
        ref={toggleBtnRef}
        aria-expanded={showFilterPanel}
        aria-haspopup="dialog"
        onClick={() => setShowFilterPanel(p => !p)}
      >
        {showFilterPanel ? 'Hide' : 'Show'} Filters
        <span className={s['visibility-count']}>
          {visibleCount} / {regionSchemes.length} schemes
        </span>
      </Button>

      {showFilterPanel && schemesByStrategy.length > 0 && pos && createPortal(
        <div
          ref={panelRef}
          className={s['visibility-panel']}
          style={{ top: pos.top, left: pos.left, maxWidth: pos.maxWidth, maxHeight: pos.maxHeight }}
          role="dialog"
          aria-label="Scheme filters"
        >
          {schemesByStrategy.map(group => {
            const groupIds = group.schemes.map(sc => sc.value);
            const allHidden = groupIds.every(id => hiddenSchemeIds.has(id));
            const someHidden = groupIds.some(id => hiddenSchemeIds.has(id));
            return (
              <div key={group.key} className={s['strategy-group']}>
                <label className={s['strategy-label']}>
                  <input
                    type="checkbox"
                    checked={!allHidden}
                    ref={el => { if (el) el.indeterminate = someHidden && !allHidden; }}
                    onChange={() => toggleStrategy(groupIds)}
                  />
                  {group.display}
                  <span className={s['strategy-count']}>{group.schemes.length}</span>
                </label>
                <div className={s['scheme-chips']}>
                  {group.schemes.map(sc => {
                    const hidden = hiddenSchemeIds.has(sc.value);
                    return (
                      <label
                        key={sc.value}
                        className={`${s['scheme-chip']} ${hidden ? s['chip-hidden'] : s['chip-visible']}`}
                      >
                        <input
                          type="checkbox"
                          checked={!hidden}
                          onChange={() => toggleScheme(sc.value)}
                        />
                        {sc.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

export default SchemeFilterPanel;